import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Container, Row, Col, Button, Form, Card, Image } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
// Update the import path to match the actual location and filename of your slice file
// import { submitHealthInsuranceForm, clearHealthInsuranceData } from '../features/healthInsurance/healthInsurance.slice';
import '../scss/health-insurance.style.scss';
import { arrowRight, health, mail, phone, user } from './images/jpg/healthInsuranceIcons';
import { submitHealthInsuranceForm } from '../features/health-insurance/health-insurance.slice';
// Asset constants from Figma


interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    address: string;
    ssn?: string;
}

export const HealthInsurance: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { healthInsuranceData, loading, error } = useAppSelector((state) => state.healthInsurance);
    const [showSuccess, setShowSuccess] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormData>();

    const onSubmit = useCallback(async (data: FormData) => {
        try {
            const language = localStorage.getItem("i18nextLng");
            const payload = {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                address: data.address,
                ...(data.ssn && { ssn: data.ssn }), // Only include SSN if provided
                lang: language || "en-US",
                type: 3,
            };

            const result = await dispatch(submitHealthInsuranceForm(payload));

            if (submitHealthInsuranceForm.fulfilled.match(result)) {
                setShowSuccess(true);
                reset();
                toast.success(t("HEALTH_INSURANCE_FORM_SUCCESS") as string);

                // Hide success message after 5 seconds
                setTimeout(() => setShowSuccess(false), 5000);
            } else {
                toast.error(t("HEALTH_INSURANCE_FORM_ERROR") as string);
            }
        } catch (error) {
            toast.error(t("HEALTH_INSURANCE_FORM_ERROR") as string);
        }
    }, [dispatch, reset, t]);

    const handleCreateAccount = () => {
        navigate('/register');
    };

    return (
        <div className="health-insurance-page px-0">
            {/* Main Content Section */}
            <section className="main-content px-0">
                <Container>
                    <Row>
                        <Col lg={6} md={12} className="mb-3 mb-lg-0 px-0">
                            <div className="content-layout">
                                <div className="content-text">
                                    <h2 className="main-title">
                                        {t("HEALTH_INSURANCE_MAIN_TITLE")}
                                    </h2>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6} md={12}>
                            <div className="content-description">
                                <p className="description-text">
                                    {t("HEALTH_INSURANCE_MAIN_DESCRIPTION")}
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Hero Image Section */}
            <section className="hero-section">
                <div className="hero-image-container">
                    <Image src={health} alt={t("HEALTH_INSURANCE")} className="hero-image" fluid />
                </div>
            </section>

            {/* Comprehensive Coverage Section */}
            <section className="comprehensive-section">
                <Container>
                    <Row className="g-0">
                        <Col xs={12}>
                            <div className="comprehensive-layout">
                                <div className="comprehensive-title">
                                    <h2 className="section-title">
                                        {t("HEALTH_INSURANCE_INFO_TITLE")}
                                    </h2>
                                </div>
                                <div className="comprehensive-content">
                                    <p className="comprehensive-text">
                                        {t("HEALTH_INSURANCE_INFO_DESCRIPTION")}
                                    </p>
                                    <Button variant="primary" className="euro-accident-btn"
                                     onClick={() => window.open("https://www.api.invozio.com/img/V%C3%A4lkomstbrevSjukf%C3%B6rs%C3%A4kringPlansjuk.pdf?_gl=1*1uffgs5*_gcl_au*MTU4ODAwMTk2NS4xNzU0MDI2NDUy*_ga*MTQyNTE0OTA3Mi4xNzU0MDI2NDUy*_ga_DJKEC77RNR*czE3NTU1MzU2NzEkbzI4JGcxJHQxNzU1NTM1Njg0JGo0NyRsMCRoMA..")}>
                                        {t("HEALTH_INSURANCE_INFO_LINK")}
                                        <Image src={arrowRight} alt="Arrow" className="arrow-icon ms-2" />
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Quote and Contact Section */}
            <section className="health-contact-section">
                <Container>
                    <Row className="g-4">
                        <Col lg={5} md={12} className="mb-4 mb-lg-0 d-flex justify-content-center justify-content-lg-start">
                            {/* Quote Form */}
                            <div className="health-form-container">
                                <Card className="health-insurance-form">
                                    <Card.Body className="d-flex flex-column">
                                        <div className="form-header">
                                            <h3 className="form-title">{t("HEALTH_INSURANCE_FORM_TITLE")}</h3>
                                            <p className="form-description">
                                                {t("HEALTH_INSURANCE_FORM_DESCRIPTION")}
                                            </p>
                                        </div>

                                        {showSuccess && (
                                            <div className="alert alert-success mb-3" role="alert">
                                                {t("HEALTH_INSURANCE_FORM_SUCCESS")}
                                            </div>
                                        )}

                                        {error && (
                                            <div className="alert alert-danger mb-3" role="alert">
                                                {error}
                                            </div>
                                        )}

                                        <Form onSubmit={handleSubmit(onSubmit)} className="health-form flex-grow-1 d-flex flex-column">
                                            <div className="form-fields flex-grow-1">
                                                <Form.Group className="mb-3">
                                                    <Form.Label htmlFor="firstName">{t("HEALTH_INSURANCE_FORM_FIRST_NAME")}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        id="firstName"
                                                        {...register("firstName", {
                                                            required: t("HEALTH_INSURANCE_FORM_FIRST_NAME_REQUIRED"),
                                                            minLength: {
                                                                value: 2,
                                                                message: t("HEALTH_INSURANCE_FORM_FIRST_NAME_MIN_LENGTH")
                                                            }
                                                        })}
                                                        placeholder={t("HEALTH_INSURANCE_FORM_FIRST_NAME_PLACEHOLDER")}
                                                        isInvalid={!!errors.firstName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.firstName?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label htmlFor="lastName">{t("HEALTH_INSURANCE_FORM_LAST_NAME")}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        id="lastName"
                                                        {...register("lastName", {
                                                            required: t("HEALTH_INSURANCE_FORM_LAST_NAME_REQUIRED"),
                                                            minLength: {
                                                                value: 2,
                                                                message: t("HEALTH_INSURANCE_FORM_LAST_NAME_MIN_LENGTH")
                                                            }
                                                        })}
                                                        placeholder={t("HEALTH_INSURANCE_FORM_LAST_NAME_PLACEHOLDER")}
                                                        isInvalid={!!errors.lastName}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lastName?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label htmlFor="email">{t("HEALTH_INSURANCE_FORM_EMAIL")}</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        id="email"
                                                        {...register("email", {
                                                            required: t("HEALTH_INSURANCE_FORM_EMAIL_REQUIRED"),
                                                            pattern: {
                                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                                message: t("HEALTH_INSURANCE_FORM_EMAIL_INVALID")
                                                            }
                                                        })}
                                                        placeholder={t("HEALTH_INSURANCE_FORM_EMAIL_PLACEHOLDER")}
                                                        isInvalid={!!errors.email}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.email?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label htmlFor="address">{t("HEALTH_INSURANCE_FORM_ADDRESS")}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        id="address"
                                                        {...register("address", {
                                                            required: t("HEALTH_INSURANCE_FORM_ADDRESS_REQUIRED"),
                                                            minLength: {
                                                                value: 5,
                                                                message: t("HEALTH_INSURANCE_FORM_ADDRESS_MIN_LENGTH")
                                                            }
                                                        })}
                                                        placeholder={t("HEALTH_INSURANCE_FORM_ADDRESS_PLACEHOLDER")}
                                                        isInvalid={!!errors.address}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.address?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label htmlFor="ssn">{t("HEALTH_INSURANCE_FORM_SSN")} ({t("OPTIONAL")})</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        id="ssn"
                                                        {...register("ssn", {
                                                            minLength: {
                                                                value: 10,
                                                                message: t("HEALTH_INSURANCE_FORM_SSN_MIN_LENGTH")
                                                            },
                                                            maxLength: {
                                                                value: 15,
                                                                message: t("HEALTH_INSURANCE_FORM_SSN_MAX_LENGTH")
                                                            }
                                                        })}
                                                        placeholder={t("HEALTH_INSURANCE_FORM_SSN_PLACEHOLDER")}
                                                        isInvalid={!!errors.ssn}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.ssn?.message}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </div>

                                            <div className="form-submit-health mt-auto">
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    className="health-submit-btn"
                                                    disabled={loading}
                                                >
                                                    {loading ? t("HEALTH_INSURANCE_FORM_SUBMITTING") : t("HEALTH_INSURANCE_FORM_SUBMIT")}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>

                        <Col lg={2} className="d-none d-lg-block">
                            {/* Spacer column for 166px gap */}
                        </Col>

                        <Col lg={5} md={12}>
                            {/* Contact Information */}
                            <div className="contact-info-container">
                                <h3 className="contact-title-health">{t("HEALTH_INSURANCE_CONTACT_TITLE")}</h3>
                                <p className="contact-subtitle">
                                    {t("HEALTH_INSURANCE_CONTACT_DESCRIPTION")}
                                </p>

                                <div className="contact-items">
                                    <div className="contact-item d-flex align-items-center mb-3">
                                        <div className="contact-icon me-3">
                                            <Image src={user} alt="User" />
                                        </div>
                                        <div className="contact-details">
                                            <h4 className="contact-label">{t("HEALTH_INSURANCE_CONTACT_SPECIALIST_TITLE")}</h4>
                                            <p className="contact-value">{t("HEALTH_INSURANCE_CONTACT_SPECIALIST_VALUE")}</p>
                                        </div>
                                    </div>

                                    <div className="contact-item d-flex align-items-center mb-3">
                                        <div className="contact-icon me-3">
                                            <Image src={phone} alt="Phone" />
                                        </div>
                                        <div className="contact-details">
                                            <h4 className="contact-label">{t("HEALTH_INSURANCE_CONTACT_PHONE_TITLE")}</h4>
                                            <p className="contact-value">{t("HEALTH_INSURANCE_CONTACT_PHONE_VALUE")}</p>
                                        </div>
                                    </div>

                                    <div className="contact-item d-flex align-items-center mb-3">
                                        <div className="contact-icon me-3">
                                            <Image src={mail} alt="Email" />
                                        </div>
                                        <div className="contact-details">
                                            <h4 className="contact-label">{t("HEALTH_INSURANCE_CONTACT_EMAIL_TITLE")}</h4>
                                            <p className="contact-value">{t("HEALTH_INSURANCE_CONTACT_EMAIL_VALUE")}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default HealthInsurance;
